import { Grid, Card, CardContent, CardHeader, Box, Typography, Button, Stack, Divider } from '@mui/material';
import { Hub, ReceiptLong, LocalShipping, AssignmentTurnedIn, Add, ArrowForward } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { MiniBarChart } from '../../components/MiniBarChart';
import { StatusBadge } from '../../components/StatusBadge';
import { EmptyState } from '../../components/EmptyState';
import { useAuth } from '../../hooks/useAuth';
import { omsApi } from '../../api/oms.api';
import { boqApi } from '../../api/boq.api';
import { procurementApi, prApi } from '../../api/procurement.api';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

export function DashboardPage() {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();

  const canOms = hasPermission('oms.read');
  const canBoq = hasPermission('boq.read');
  const canPo = hasPermission('po.read');
  const canPr = hasPermission('pr.read');

  const oms = useQuery({ queryKey: ['dash-oms'], queryFn: omsApi.dashboard, enabled: canOms });
  const boq = useQuery({ queryKey: ['dash-boq'], queryFn: boqApi.dashboard, enabled: canBoq });
  const proc = useQuery({ queryKey: ['dash-proc'], queryFn: procurementApi.dashboard, enabled: canPo });
  const recentOms = useQuery({ queryKey: ['dash-recent-oms'], queryFn: () => omsApi.list({ limit: 5 }), enabled: canOms });
  const pendingPr = useQuery({ queryKey: ['dash-pending-pr'], queryFn: () => prApi.list({ limit: 5, status: 'pending_approval' }), enabled: canPr });

  const omsStatusData = oms.data
    ? Object.entries(oms.data.byStatus).map(([label, value]) => ({ label, value: value as number, color: '#1e40af' }))
    : [];
  const procStatusData = proc.data?.prStatusCounts
    ? Object.entries(proc.data.prStatusCounts).map(([label, value]) => ({ label, value: value as number, color: '#0d9488' }))
    : [];

  const quickActions = [
    { label: 'New OMS', path: '/oms', show: hasPermission('oms.create') },
    { label: 'New BOQ', path: '/boqs', show: hasPermission('boq.create') },
    { label: 'New Requisition', path: '/purchase-requisitions', show: hasPermission('pr.create') },
    { label: 'New Client', path: '/clients', show: hasPermission('client.create') },
  ].filter((a) => a.show);

  return (
    <>
      <PageHeader title={`Welcome back, ${user?.fullName?.split(' ')[0] ?? ''}`.trim()} subtitle="Here's an operational snapshot across MyevCRM." />

      {/* KPI cards */}
      <Grid container spacing={2} mb={1}>
        {canOms && <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#1e40af" label="Total OMS" value={oms.data?.total ?? '—'} icon={<Hub />} /></Grid>}
        {canBoq && <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#0d9488" label="BOQ Client Value" value={inr(boq.data?.totalClientValue)} icon={<ReceiptLong />} hint={`${boq.data?.totalBoqs ?? 0} BOQs`} /></Grid>}
        {canPo && <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#7c3aed" label="PO Value" value={inr(proc.data?.poValue)} icon={<LocalShipping />} /></Grid>}
        {canPo && <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#d97706" label="Pending Procurement" value={proc.data?.pendingProcurement ?? '—'} icon={<AssignmentTurnedIn />} hint={inr(proc.data?.costSaving) + ' saved'} /></Grid>}
      </Grid>

      <Grid container spacing={2}>
        {/* Charts */}
        {canOms && (
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardHeader title="OMS by Status" titleTypographyProps={{ variant: 'subtitle1' }} />
              <CardContent>{oms.data ? <MiniBarChart data={omsStatusData} /> : <EmptyState title="Loading…" dense />}</CardContent>
            </Card>
          </Grid>
        )}
        {canPo && (
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardHeader title="Procurement Pipeline" titleTypographyProps={{ variant: 'subtitle1' }} />
              <CardContent>{proc.data ? <MiniBarChart data={procStatusData} /> : <EmptyState title="Loading…" dense />}</CardContent>
            </Card>
          </Grid>
        )}

        {/* Pending approvals */}
        {canPr && (
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardHeader
                title="Pending Approvals"
                titleTypographyProps={{ variant: 'subtitle1' }}
                action={<Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/purchase-requisitions')}>View</Button>}
              />
              <Divider />
              <CardContent sx={{ p: 0 }}>
                {(pendingPr.data?.data.length ?? 0) === 0 ? (
                  <EmptyState title="Nothing awaiting approval" dense />
                ) : (
                  <Stack divider={<Divider />}>
                    {pendingPr.data?.data.map((pr) => (
                      <Box key={pr.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25 }}>
                        <Box minWidth={0}>
                          <Typography variant="body2" fontWeight={600} noWrap>{pr.code}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>{pr.title}</Typography>
                        </Box>
                        <StatusBadge status={pr.status} />
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Quick actions + recent */}
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            {quickActions.length > 0 && (
              <Card>
                <CardHeader title="Quick Actions" titleTypographyProps={{ variant: 'subtitle1' }} />
                <CardContent>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {quickActions.map((a) => (
                      <Button key={a.path} variant="outlined" startIcon={<Add />} onClick={() => navigate(a.path)}>{a.label}</Button>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}
            {canOms && (
              <Card>
                <CardHeader title="Recent OMS" titleTypographyProps={{ variant: 'subtitle1' }} />
                <Divider />
                <CardContent sx={{ p: 0 }}>
                  {(recentOms.data?.data.length ?? 0) === 0 ? (
                    <EmptyState title="No OMS yet" dense />
                  ) : (
                    <Stack divider={<Divider />}>
                      {recentOms.data?.data.map((o) => (
                        <Box key={o.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25, cursor: 'pointer' }} onClick={() => navigate('/oms')}>
                          <Box minWidth={0}>
                            <Typography variant="body2" fontWeight={600} noWrap>{o.omsNumber}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>{o.title}</Typography>
                          </Box>
                          <StatusBadge status={o.status} />
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>
    </>
  );
}
